import {ImmutableObject} from '../../utils/immutableObject';
import {EnumOpportunityStatus} from '../enum/enum-opportunity-status';
import {ClientInfos} from './client-informations';
import {EnumQuoteType} from '../enum/enum-quote-type';
import {EnumSalesFileStatus} from '../enum/enum-sales-file-status';
import Big from 'big.js';
import {bigToString, parseBig} from '../../utils/math-utils';
import {EnumCorridorColor} from '../enum/enum-corridor-color';
import {EnumClientSize} from '../enum/enum-client-size';
import {CoefficientBoost} from 'rem-pct-engine-core';
import {EmployeeRoleEnum} from '../employee/employee-role.enum';
import {ServiceLine} from './service-line';
import {ProductLineModel} from '../opportunity-context/opportunity-context.model';
import {EnumOpportunityType} from '../enum/enum-opportunity-type';

export class QuoteWrapper extends ImmutableObject {
    constructor(
        public readonly id?: string,
        public readonly reference?: string,
        public readonly serviceLines?: ServiceLine[],
        public readonly clientInfos?: ClientInfos,
        public readonly type?: EnumQuoteType,
        public readonly creationDate?: Date,
        public readonly countryIsoCode?: string,
        public readonly standardPricingItemVersion?: string,
        public readonly regionCode?: string,
        public readonly regionLabel?: string,
        public readonly teamCode?: string,
        public readonly teamLabel?: string,
        public readonly opportunityId?: number,
        public readonly opportunityOfflineId?: string,
        public readonly opportunityLabel?: string,
        public readonly opportunityStatus?: EnumOpportunityStatus,
        public readonly opportunityType?: EnumOpportunityType,
        public readonly employeeRole?: EmployeeRoleEnum,
        public readonly isMainQuote?: boolean,
        public readonly isDuplicated?: boolean,
        public readonly opportunitySalesFileStatus?: EnumSalesFileStatus,
        public readonly nonNegociableArticleColor?: string,
        public readonly currency?: string,
        public readonly color?: EnumCorridorColor,
        public readonly eforceOpportunityId?: string,
        public readonly opportunityCreationDate?: Date,
        public readonly clientSize?: EnumClientSize,
        public readonly contractId?: string,
        public readonly contractReference?: string,
        public readonly nonReferencedCmcProtocolCode?: string,
        public readonly coefficientBoosts?: CoefficientBoost[],
        public readonly existingColor?: EnumCorridorColor,
        public readonly expectedProductLines?: ProductLineModel[],
        public readonly initialQuoteCreationDate?: Date,
        public readonly locale?: string,
        public readonly speciality?: string
    ) {
        super();
    }

    get isValid(): boolean {
        return this.serviceLines.every(serviceLine => serviceLine.isServiceLineValid(this.employeeRole.isDisco()));
    }

    getServiceLineByCode(serviceCode: string): ServiceLine {
        const lineIndex = this.serviceLines.findIndex(serv => serv.code === serviceCode);
        if (lineIndex != -1) {
            return this.serviceLines[lineIndex];
        }
    }


    getTotalPct(): string {
        let pct: Big = new Big('0');
        this.serviceLines.forEach(line => {
            pct = pct.plus(parseBig(line.newQuoteService.pct));
        });
        return bigToString(pct);
    }

    getTotalDeltaPctRounded(): number {
        let total: number = 0;
        this.serviceLines.forEach(service => {
            total += Math.round(Number(service.newQuoteService.deltaPctWithOneMoreService));
        });
        return total;
    }

    isPctMultiServiceBoostEnabled(): boolean {
        return this.serviceLines.some(quoteService => !!quoteService.newQuoteService.multiServiceBoost);
    }

    hasAtLeastOnePricingItem() {
        return this.serviceLines.some(sl => sl.hasAtLeastOnePricingItem());
    }

    get variationPrice(): string {
        let total: Big = new Big(this.totalMonthlyAmount).minus(new Big(this.existingTotalMonthlyAmount));
        return bigToString(total);
    }

    get totalWeeklyAmount(): string {
        return bigToString(this.serviceLines.reduce((acc, serviceLine) => {
            const weeklyAmount = serviceLine.weeklyAmount;
            return acc.plus(parseBig(weeklyAmount));
        }, new Big(0)));
    }

    get totalMonthlyAmount(): string {
        return bigToString(this.serviceLines.reduce((acc, serviceLine) => {
            const monthlyAmount = serviceLine.monthlyAmount;
            return acc.plus(parseBig(monthlyAmount));
        }, new Big(0)));
    }


    get existingTotalMonthlyAmount(): string {
        return bigToString(this.serviceLines.reduce((acc, serviceLine) => {
            const monthlyAmount = serviceLine.existingMonthlyAmount;
            return acc.plus(parseBig(monthlyAmount));
        }, new Big(0)));
    }

    isCorrectlyPriced(): boolean {
        const allExpectedServicesCorrectlyPriced = this.expectedProductLines.every(productLine => {
            const associatedServices = this.serviceLines.filter(serviceLine =>
                productLine.isServiceCodeMatchProductLine(serviceLine.code)
            );
            return associatedServices.some(serviceLine => serviceLine.isServicePriced());
        });

        const noUnexpectedServicePriced = this.serviceLines.every(serviceLine => {
            const isServiceExpected = this.expectedProductLines.some(productLine =>
                productLine.isServiceCodeMatchProductLine(serviceLine.code)
            );
            return isServiceExpected || !serviceLine.isServicePriced();
        });

        return allExpectedServicesCorrectlyPriced && noUnexpectedServicePriced;
    }
}
